// Secure storage utilities with proper encryption
import { escapeHtml } from './security'

interface SecureStorageOptions {
  encrypt?: boolean
  ttl?: number // Time to live in milliseconds
}

interface StoredData {
  data: string
  timestamp: number
  ttl?: number
}

class SecureStorage {
  private static instance: SecureStorage
  private encryptionKey: CryptoKey | null = null
  private static readonly STORAGE_PREFIX = 'kbs_secure_'

  private constructor() {
    this.initializeEncryptionKey()
  }

  static getInstance(): SecureStorage {
    if (!SecureStorage.instance) {
      SecureStorage.instance = new SecureStorage()
    }
    return SecureStorage.instance
  }

  private async initializeEncryptionKey(): Promise<void> {
    try {
      // Try to get encryption key from environment
      const envKey = import.meta.env.VITE_ENCRYPTION_KEY
      
      if (envKey) {
        // Use environment key
        const keyData = new TextEncoder().encode(envKey)
        this.encryptionKey = await crypto.subtle.importKey(
          'raw',
          keyData,
          { name: 'PBKDF2' },
          false,
          ['deriveKey']
        )
      } else {
        // Generate a key based on browser fingerprint
        const fingerprint = await this.generateBrowserFingerprint()
        const keyData = new TextEncoder().encode(fingerprint)
        
        this.encryptionKey = await crypto.subtle.importKey(
          'raw',
          keyData,
          { name: 'PBKDF2' },
          false,
          ['deriveKey']
        )
      }
    } catch (error) {
      console.error('Failed to initialize encryption key:', error)
      this.encryptionKey = null
    }
  }

  private async generateBrowserFingerprint(): Promise<string> {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.textBaseline = 'top'
      ctx.font = '14px Arial'
      ctx.fillText('Browser fingerprint', 2, 2)
    }
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      canvas.toDataURL(),
      navigator.hardwareConcurrency || 'unknown',
      navigator.maxTouchPoints || 'unknown'
    ].join('|')
    
    return await this.hashString(fingerprint)
  }

  private async hashString(str: string): Promise<string> {
    const encoder = new TextEncoder()
    const data = encoder.encode(str)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
  }

  private async deriveKey(password: string, salt: ArrayBuffer): Promise<CryptoKey> {
    if (!this.encryptionKey) {
      throw new Error('Base encryption key not available')
    }
    
    const passwordKey = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(password),
      { name: 'PBKDF2' },
      false,
      ['deriveKey']
    )
    
    return await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 100000,
        hash: 'SHA-256'
      },
      passwordKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['encrypt', 'decrypt']
    )
  }

  private async encrypt(data: string): Promise<string> {
    if (!this.encryptionKey) {
      throw new Error('Encryption key not available')
    }
    
    try {
      // Generate random salt and IV
      const salt = crypto.getRandomValues(new Uint8Array(16))
      const iv = crypto.getRandomValues(new Uint8Array(12))
      
      // Derive key from base key and salt
      const derivedKey = await this.deriveKey('kbs-lms-storage', salt.buffer)
      
      // Encrypt the data
      const encoder = new TextEncoder()
      const dataBuffer = encoder.encode(data)
      
      const encryptedBuffer = await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv },
        derivedKey,
        dataBuffer
      )
      
      // Combine salt, IV, and encrypted data
      const combined = new Uint8Array(salt.length + iv.length + encryptedBuffer.byteLength)
      combined.set(salt, 0)
      combined.set(iv, salt.length)
      combined.set(new Uint8Array(encryptedBuffer), salt.length + iv.length)
      
      // Convert to base64
      return btoa(String.fromCharCode(...combined))
    } catch (error) {
      console.error('Encryption failed:', error)
      throw new Error('Failed to encrypt data')
    }
  }

  private async decrypt(encryptedData: string): Promise<string> {
    if (!this.encryptionKey) {
      throw new Error('Encryption key not available')
    }
    
    try {
      // Decode from base64
      const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0))
      
      // Extract salt, IV, and encrypted data
      const salt = combined.slice(0, 16)
      const iv = combined.slice(16, 28)
      const encrypted = combined.slice(28)
      
      // Derive key from base key and salt
      const derivedKey = await this.deriveKey('kbs-lms-storage', salt.buffer)
      
      // Decrypt the data
      const decryptedBuffer = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: iv },
        derivedKey,
        encrypted
      )
      
      // Convert back to string
      const decoder = new TextDecoder()
      return decoder.decode(decryptedBuffer)
    } catch (error) {
      console.error('Decryption failed:', error)
      throw new Error('Failed to decrypt data')
    }
  }

  private isExpired(timestamp: number, ttl?: number): boolean {
    if (!ttl) return false
    return Date.now() - timestamp > ttl
  }

  private getStorageKey(key: string): string {
    return `${SecureStorage.STORAGE_PREFIX}${escapeHtml(key)}`
  }

  async setItem(key: string, value: any, options: SecureStorageOptions = {}): Promise<void> {
    try {
      const { encrypt = true, ttl } = options
      
      const dataToStore: StoredData = {
        data: typeof value === 'string' ? value : JSON.stringify(value),
        timestamp: Date.now(),
        ttl
      }
      
      let serializedData = JSON.stringify(dataToStore)
      
      if (encrypt) {
        serializedData = await this.encrypt(serializedData)
      }
      
      const storageKey = this.getStorageKey(key)
      localStorage.setItem(storageKey, serializedData)
    } catch (error) {
      console.error('Failed to store data:', error)
      throw new Error('Failed to store data securely')
    }
  }

  async getItem<T = any>(key: string, options: SecureStorageOptions = {}): Promise<T | null> {
    try {
      const { encrypt = true } = options
      const storageKey = this.getStorageKey(key)
      const storedData = localStorage.getItem(storageKey)
      
      if (!storedData) {
        return null
      }
      
      let parsedData: StoredData
      
      if (encrypt) {
        const decryptedData = await this.decrypt(storedData)
        parsedData = JSON.parse(decryptedData)
      } else {
        parsedData = JSON.parse(storedData)
      }
      
      // Check if data is expired
      if (this.isExpired(parsedData.timestamp, parsedData.ttl)) {
        this.removeItem(key)
        return null
      }
      
      // Try to parse as JSON, fallback to string
      try {
        return JSON.parse(parsedData.data)
      } catch {
        return parsedData.data as T
      }
    } catch (error) {
      console.error('Failed to retrieve data:', error)
      return null
    }
  }

  removeItem(key: string): void {
    const storageKey = this.getStorageKey(key)
    localStorage.removeItem(storageKey)
  }

  clear(): void {
    const keys = Object.keys(localStorage)
    keys.forEach(key => {
      if (key.startsWith(SecureStorage.STORAGE_PREFIX)) {
        localStorage.removeItem(key)
      }
    })
  }

  // Clean up expired items
  async cleanup(): Promise<void> {
    const keys = Object.keys(localStorage)
    const expiredKeys: string[] = []
    
    for (const key of keys) {
      if (key.startsWith(SecureStorage.STORAGE_PREFIX)) {
        try {
          const storedData = localStorage.getItem(key)
          if (storedData) {
            const parsedData = JSON.parse(storedData)
            if (this.isExpired(parsedData.timestamp, parsedData.ttl)) {
              expiredKeys.push(key)
            }
          }
        } catch {
          // If we can't parse the data, consider it corrupted and remove it
          expiredKeys.push(key)
        }
      }
    }
    
    expiredKeys.forEach(key => localStorage.removeItem(key))
  }

  // Get all stored keys
  getKeys(): string[] {
    const keys = Object.keys(localStorage)
    return keys
      .filter(key => key.startsWith(SecureStorage.STORAGE_PREFIX))
      .map(key => key.substring(SecureStorage.STORAGE_PREFIX.length))
  }

  // Check if key exists
  hasKey(key: string): boolean {
    const storageKey = this.getStorageKey(key)
    return localStorage.getItem(storageKey) !== null
  }

  // Get storage usage info
  getStorageInfo(): { used: number; available: number; percentage: number } {
    let used = 0
    const keys = Object.keys(localStorage)
    
    keys.forEach(key => {
      if (key.startsWith(SecureStorage.STORAGE_PREFIX)) {
        const value = localStorage.getItem(key)
        if (value) {
          used += key.length + value.length
        }
      }
    })
    
    // Estimate available space (this is approximate)
    const available = 5 * 1024 * 1024 - used // 5MB estimate
    const percentage = (used / (5 * 1024 * 1024)) * 100
    
    return { used, available, percentage }
  }
}

// Export singleton instance
export const secureStorage = SecureStorage.getInstance()

// Export class for testing
export { SecureStorage }